import { useEffect, useState } from 'react';
import { TariffSectionApiModel } from '~/lib/api/smartsend/types';
import Loading from '../../../../components/loading/Loading';
import { CategorySelection } from './CategorySelection';
import { TariffSections } from './TariffSections';
import { TariffSuggestions } from './TariffSuggestions';
import { TariffSuggestionSearch } from './TariffSuggestionSearch';
import { TariffSelectionType } from './TariffSelectionType';
import { TariffSelectorProps } from './TariffSelectorProps';
import { P2GBox, P2GButtonGroup, P2GButtonGroupItem, P2GAlert, P2GText, P2GFade, P2GGrid } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

const TariffSelectorPanel: React.FC<TariffSelectorProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getTariffCodeClassifications, getTariffCodeSections } = useSmartSendApi();
  const [tab, setTab] = useState<TariffSelectionType>(props.default || 'SUGGEST');
  const [loading, setLoading] = useState(false);
  const [sections, setSection] = useState<TariffSectionApiModel[]>([]);

  const onSelect = (code: string) => {
    if (props.selectCode) {
      props.selectCode(code.replaceAll(' ', ''));
    }
  };

  const getClassifications = async (code: string) => {
    return getTariffCodeClassifications(props.system, code).then((r) => {
      return r.success ? r.result : [];
    });
  };

  const renderTitle = (x: TariffSelectionType) => {
    switch (x) {
      case 'SUGGEST':
        return 'Suggestions';
      case 'SEARCH':
        return 'Search';
      case 'BROWSE':
        return 'Browse';
      default:
        return x;
    }
  };

  useEffect(() => {
    setLoading(true);
    getTariffCodeSections(props.system).then((r) => {
      setSection(r.success ? r.result : []);
      setLoading(false);
    });
  }, []);

  return (
    <>
      <P2GBox mb={2}>
        <P2GButtonGroup fullWidth>
          {props.options.map((o) => (
            <P2GButtonGroupItem key={o} value={o} selected={tab} onClick={setTab}>
              {t(`TARIFF_SELECTOR_${o}_TAB`, renderTitle(o))}
            </P2GButtonGroupItem>
          ))}
        </P2GButtonGroup>
      </P2GBox>

      {!props.canSelect && (
        <P2GBox mb={2}>
          <P2GAlert color="info">
            <P2GText variant="body2">
              Click on '<strong>Copy</strong>' to add the tariff code to your clipboard.
            </P2GText>
          </P2GAlert>
        </P2GBox>
      )}

      {tab === 'SUGGEST' && (
        <P2GFade in>
          <P2GGrid container direction="column" spacing={3}>
            <P2GGrid item>
              <TariffSuggestions
                canSelect={props.canSelect}
                limit={5}
                selectCode={onSelect}
                tariffSystem={props.system}
                itemDescription={props.searchTerm}
                showAllOnLoad
              />
            </P2GGrid>
            <P2GGrid item>
              <CategorySelection exit={() => setTab('SEARCH')} onSelect={onSelect} selectedCode={props.tariffCode}>
                <P2GBox mb={3}>
                  <P2GText>To help you find the correct tariff code, choose one of the categories below.</P2GText>
                </P2GBox>
              </CategorySelection>
            </P2GGrid>
          </P2GGrid>
        </P2GFade>
      )}

      {tab === 'SEARCH' && (
        <P2GFade in>
          <P2GBox>
            <TariffSuggestionSearch
              canSelect={props.canSelect}
              selectCode={onSelect}
              searchTerm={props.searchTerm}
              tariffSystem={props.system}
            />
          </P2GBox>
        </P2GFade>
      )}

      {tab === 'BROWSE' && (
        <P2GFade in>
          <P2GBox>
            {loading ? (
              <Loading />
            ) : (
              <TariffSections
                canSelect={props.canSelect}
                sections={sections}
                getClassifications={getClassifications}
                selectCode={onSelect}
              />
            )}
          </P2GBox>
        </P2GFade>
      )}
    </>
  );
};

export default TariffSelectorPanel;
